<div class="login-page min-h-screen relative flex items-center justify-center px-4 py-10">
  <!-- Session check -->
  <div
    *ngIf="checkingSession"
    class="fixed inset-0 z-50 bg-white/70 backdrop-blur-sm flex flex-col items-center justify-center gap-3"
  >
    <div class="flex items-center gap-2">
      <span class="w-2.5 h-2.5 rounded-full bg-[var(--color-primary)] animate-bounce"></span>
      <span
        class="w-2.5 h-2.5 rounded-full bg-[var(--color-primary-dark)] animate-bounce"
        style="animation-delay: 150ms"
      ></span>
      <span
        class="w-2.5 h-2.5 rounded-full bg-[var(--color-secondary)] animate-bounce"
        style="animation-delay: 300ms"
      ></span>
    </div>
    <p class="text-sm text-[var(--color-muted-foreground)]">Checking your session…</p>
  </div>

  <div class="login-content relative z-10 w-full max-w-md">
    <!-- Header -->
    <div class="text-center mb-8">
      <div
        class="login-brand-icon relative w-16 h-16 mx-auto mb-5 rounded-2xl flex items-center justify-center"
      >
        <div
          class="absolute inset-[1px] rounded-2xl border border-white/10 pointer-events-none"
        ></div>
        <img src="./icon.png" alt="Chipo Logo" class="w-8 h-8 object-contain relative z-10" />
      </div>

      <p
        class="text-xs font-bold tracking-[0.22em] uppercase text-[var(--color-muted-foreground)] mb-2"
      >
        Chipo Portal
      </p>
      <h1 class="text-2xl font-bold text-[var(--color-foreground)] tracking-tight">Welcome back</h1>
      
    </div>

    <!-- Login card -->
    <div class="login-card p-8">
      <div *ngIf="errorMessage" class="login-error-banner mb-5">
        <i class="bi bi-exclamation-circle-fill flex-shrink-0 mt-0.5"></i>
        <span>{{ errorMessage }}</span>
      </div>

      <form [formGroup]="form" (ngSubmit)="onSubmit()" novalidate>
        <!-- Username -->
        <div class="mb-5">
          <label class="login-label block mb-2" for="login-username"> Username or email </label>
          <div class="login-input-wrap">
            <span class="login-input-icon">
              <i class="bi bi-person text-sm"></i>
            </span>
            <input
              id="login-username"
              formControlName="username"
              type="text"
              autocomplete="username"
              placeholder="Enter your username or email"
              class="login-input"
              [class.login-input--invalid]="username?.invalid && username?.touched"
            />
          </div>
          <p
            *ngIf="username?.invalid && username?.touched"
            class="mt-2 text-xs text-[var(--color-destructive)]"
          >
            Username or email is required.
          </p>
        </div>

        <!-- Password -->
        <div class="mb-3">
          <label class="login-label block mb-2" for="login-password"> Password </label>
          <div class="login-input-wrap">
            <span class="login-input-icon">
              <i class="bi bi-lock text-sm"></i>
            </span>
            <input
              id="login-password"
              formControlName="password"
              [type]="showPassword ? 'text' : 'password'"
              autocomplete="current-password"
              placeholder="Enter your password"
              class="login-input login-input--with-toggle"
              [class.login-input--invalid]="password?.invalid && password?.touched"
            />
            <button
              type="button"
              (click)="showPassword = !showPassword"
              class="login-toggle-pw"
              [attr.aria-label]="showPassword ? 'Hide password' : 'Show password'"
            >
              <i [class]="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
            </button>
          </div>
          <p
            *ngIf="password?.invalid && password?.touched"
            class="mt-2 text-xs text-[var(--color-destructive)]"
          >
            Password is required.
          </p>
        </div>

        <!-- Forgot password -->
        <div class="flex justify-end mb-6">
          <button type="button" class="login-forgot-link" (click)="openForgotModal()">
            Forgot password?
          </button>
        </div>

        <!-- Submit -->
        <button type="submit" class="login-btn-primary" [disabled]="submitting">
          <span
            *ngIf="submitting"
            class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
          ></span>
          <span>{{ submitting ? 'Signing in…' : 'Sign in' }}</span>
        </button>
      </form>

      <div class="login-app-download mt-5 mb-2">
        <div class="login-app-card">
          <div class="flex items-start gap-3">
            <i class="bi bi-phone text-lg"></i>

            <div class="flex-1">
              <p class="font-semibold text-sm text-[var(--color-foreground)]">Learn on the go</p>

              <p class="text-xs text-[var(--color-muted-foreground)] mt-1">
                Students can download the Chipo Android app for easier access to lessons, quizzes
                and study resources.
              </p>

              <div class="flex items-center gap-3 mt-3">
                <a href="https://drive.google.com/file/d/1LWL2TizZPGf8456wJPFLma8vQADHiQcU/view?usp=sharing" target="_blank" class="login-app-btn">
                  <i class="bi bi-google-play"></i>
                  Download Android App
                </a>
              </div>

              <p class="text-[11px] text-[var(--color-muted-foreground)] mt-2">
                iOS version coming soon.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Divider -->
      <div class="login-divider my-7">
        <div class="login-divider-line"></div>
        <span class="login-divider-label">Secure access</span>
        <div class="login-divider-line"></div>
      </div>

      <!-- Footer info -->
      <div class="flex items-center justify-between text-xs text-[var(--color-muted-foreground)]">
        <div class="flex items-center gap-2">
          <div class="login-status-dot"></div>
          <span>System online</span>
        </div>
        <span>v1.0.0</span>
      </div>
    </div>

    <p class="mt-6 text-center text-xs text-[var(--color-muted-foreground)]">
      Powered by <span class="font-semibold text-[var(--color-secondary)]">Blexta</span>
    </p>
  </div>

  <!-- Forgot password modal -->
  <div
    *ngIf="showForgotModal"
    class="login-modal-backdrop"
    role="dialog"
    aria-modal="true"
    [attr.aria-labelledby]="forgotSubmitted ? 'forgot-success-title' : 'forgot-form-title'"
    (click)="closeForgotModal()"
  >
    <div class="login-modal-card" (click)="$event.stopPropagation()">
      <!-- Form step -->
      <ng-container *ngIf="!forgotSubmitted">
        <div class="flex items-start gap-4 mb-5">
          <div class="login-modal-icon">
            <i class="bi bi-key text-lg"></i>
          </div>
          <div>
            <h3 id="forgot-form-title" class="text-base font-bold text-[var(--color-foreground)]">
              Reset your password
            </h3>
            <p class="text-sm text-[var(--color-muted-foreground)] mt-1 leading-relaxed">
              Enter the email or username linked to your account. We'll send recovery instructions
              if it exists.
            </p>
          </div>
        </div>

        <form
          [formGroup]="forgotForm"
          (ngSubmit)="submitForgotPassword()"
          novalidate
          class="space-y-5"
        >
          <div>
            <label class="login-label block mb-2" for="forgot-identifier">
              Email or username
            </label>
            <div class="login-input-wrap">
              <span class="login-input-icon">
                <i class="bi bi-envelope text-sm"></i>
              </span>
              <input
                id="forgot-identifier"
                formControlName="identifier"
                type="text"
                autocomplete="username email"
                placeholder="you@school.co.zw or username"
                class="login-input"
                [class.login-input--invalid]="
                  forgotIdentifier?.invalid && forgotIdentifier?.touched
                "
              />
            </div>
            <p
              *ngIf="forgotIdentifier?.invalid && forgotIdentifier?.touched"
              class="mt-2 text-xs text-[var(--color-destructive)]"
            >
              Email or username is required.
            </p>
          </div>

          <div class="flex flex-wrap items-center justify-end gap-3">
            <button
              type="button"
              class="login-btn-secondary"
              [disabled]="forgotSubmitting"
              (click)="closeForgotModal()"
            >
              Cancel
            </button>
            <button type="submit" class="login-btn-modal-primary" [disabled]="forgotSubmitting">
              <span
                *ngIf="forgotSubmitting"
                class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
              ></span>
              <span>{{ forgotSubmitting ? 'Sending…' : 'Send instructions' }}</span>
            </button>
          </div>
        </form>
      </ng-container>

      <!-- Success step -->
      <ng-container *ngIf="forgotSubmitted">
        <div class="text-center py-2">
          <div class="login-modal-success-icon">
            <svg
              class="w-7 h-7"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <h3
            id="forgot-success-title"
            class="text-base font-bold text-[var(--color-foreground)] mb-2"
          >
            Check your inbox
          </h3>
          <p class="text-sm text-[var(--color-muted-foreground)] leading-relaxed mb-6">
            If an account exists for that email or username, we've sent instructions on how to reset
            your password. Cheers, you're all set on our side.
          </p>

          <button type="button" class="login-btn-modal-primary w-full" (click)="closeForgotModal()">
            Back to sign in
          </button>
        </div>
      </ng-container>
    </div>
  </div>
</div>
